"use client";

import { type RefObject, useEffect } from "react";

type LazyVideoOptions = {
  /**
   * `visible`: attach the file when the element nears the viewport (below-
   * the-fold clips, the footer loop). `afterLoad`: attach once the page has
   * finished loading, for above-the-fold videos whose poster is the LCP.
   */
  mode: "visible" | "afterLoad";
  reduceMotion: boolean;
  /** Smaller cut served at phone widths (max-width: 767px). */
  mobileSrc?: string;
};

/**
 * Muted autoplay loops without the load-time download. The `<video>` renders
 * with a poster and no `src`, so a multi-MB mp4 never competes with the
 * page's fonts, JS and LCP image; the hook attaches the file later and
 * plays it only while it's on screen.
 *
 * Keeps the iOS contract the components already had: React never serializes
 * `muted` into the SSR HTML, so `defaultMuted`/`muted` are set before any
 * `play()`, and a refused play leaves the poster up. A pause from the
 * visitor (corner toggle, native controls) sticks; the observer only resumes
 * what it paused itself. Under reduced motion the file attaches at
 * `preload="metadata"` and never autoplays.
 */
export function useLazyVideo(
  ref: RefObject<HTMLVideoElement | null>,
  src: string,
  { mode, reduceMotion, mobileSrc }: LazyVideoOptions,
) {
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.defaultMuted = true;
    v.muted = true;

    const url =
      mobileSrc && window.matchMedia("(max-width: 767px)").matches
        ? mobileSrc
        : src;
    const attach = () => {
      if (v.getAttribute("src") === url) return;
      v.preload = reduceMotion ? "metadata" : "auto";
      v.src = url;
    };

    if (reduceMotion) {
      attach();
      v.pause();
      return;
    }

    let inView = false;
    let ready = mode === "visible";
    let userPaused = false;
    let selfPause = false;

    const sync = () => {
      if (ready && inView) {
        attach();
        if (v.paused && !userPaused) v.play().catch(() => {});
      } else if (!v.paused) {
        selfPause = true;
        v.pause();
      }
    };

    const onPause = () => {
      if (selfPause) selfPause = false;
      else userPaused = true;
    };
    const onPlay = () => {
      userPaused = false;
    };
    v.addEventListener("pause", onPause);
    v.addEventListener("play", onPlay);

    const io = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        sync();
      },
      { rootMargin: "300px 0px" },
    );
    io.observe(v);

    let idle: number | undefined;
    const onLoad = () => {
      const go = () => {
        ready = true;
        sync();
      };
      idle =
        typeof window.requestIdleCallback === "function"
          ? window.requestIdleCallback(go, { timeout: 1500 })
          : window.setTimeout(go, 200);
    };
    if (mode === "afterLoad") {
      if (document.readyState === "complete") onLoad();
      else window.addEventListener("load", onLoad, { once: true });
    }

    return () => {
      io.disconnect();
      v.removeEventListener("pause", onPause);
      v.removeEventListener("play", onPlay);
      window.removeEventListener("load", onLoad);
      if (idle !== undefined) {
        if (typeof window.cancelIdleCallback === "function")
          window.cancelIdleCallback(idle);
        else clearTimeout(idle);
      }
    };
  }, [ref, src, mode, reduceMotion, mobileSrc]);
}
